import { getControllerApiVersion } from './utils.js';

const ESP32_S3_INTERNAL_STORAGE_API_MIN = 'BAG';
const ROOTSENSE_FIRMWARE_API_MIN = 'BAICA';
const SYSTEM_INFO_CONTROLS_API_MIN = 'BAICC';

export const extensionFirmwareFields = ['root_sense_link', 'power_extender'].flatMap((device) =>
  ['flags', 'major', 'minor', 'patch'].map((part) => `${device}_fw_${part}`)
).concat('root_sense_link_hw_variant');

export const systemTelemetryFields = extensionFirmwareFields.concat([
  'esp32_chip_type',
  'maincontroller_flash_health',
  'maincontroller_flash_status'
]);

export const systemRestartActions = ['action_restart_wifi_module', 'action_restart_maincontroller'];

export function supportsSystemInfoControls(controller) {
  return [5052, 5053, 5055, 5155].includes(Number(controller?.type)) &&
    getControllerApiVersion(controller).localeCompare(SYSTEM_INFO_CONTROLS_API_MIN) >= 0;
}

export const controllerDateFields = ['time_cont_year', 'time_cont_month', 'time_cont_day'];
export const controllerTimeFields = ['time_cont_hour', 'time_cont_min', 'time_cont_sec'];
export const controllerClockFields = [...controllerDateFields, ...controllerTimeFields];
export const controllerClockDraftFields = [...controllerClockFields, '_date_changed', '_time_changed',
  'controller_date', 'controller_time'];

export function supportsSystemClockRelocation(controller) {
  return supportsSystemInfoControls(controller);
}

export function withoutControllerClock(settings) {
  const result = { ...settings };
  controllerClockDraftFields.forEach((key) => delete result[key]);
  return result;
}

// Date and time are independent edits. Unchanged groups must never reset the RTC
// when another System setting is saved.
export function systemClockForSubmission(settings) {
  const result = { ...settings };
  if (!settings._date_changed) {
    result.time_cont_year = 0xffff;
    result.time_cont_month = result.time_cont_day = 0xff;
  }
  if (!settings._time_changed) {
    result.time_cont_hour = result.time_cont_min = result.time_cont_sec = 0xff;
  }
  delete result._date_changed;
  delete result._time_changed;
  delete result.controller_date;
  delete result.controller_time;
  return result;
}

export function controllerClockEdit(value, date) {
  const fields = date ? controllerDateFields : controllerTimeFields;
  return [
    [...fields, date ? '_date_changed' : '_time_changed'],
    [...(date ? [value.getFullYear(), value.getMonth() + 1, value.getDate()]
      : [value.getHours(), value.getMinutes(), value.getSeconds()]), true]
  ];
}

export function controllerClockDisplayValues(controller) {
  // Every zone reports the same maincontroller RTC in current values. Prefer
  // that live source when opening/resetting the editor; System category readback
  // can predate the latest current-values frame. System-only readers may have
  // no zone current values, in which case their canonical System readback applies.
  const source = [controller, ...Object.values(controller.zones ?? {})]
    .find((parent) => parent.infos?.time && parent.infos?.date);
  if (!source) return Object.fromEntries(controllerClockFields.map((key) =>
    [key, controller.settings.system[key]]));
  const [hour, min, sec] = source.infos.time.split(':').map(Number);
  const [day, month, year] = source.infos.date.split('.').map(Number);
  return { time_cont_hour: hour, time_cont_min: min, time_cont_sec: sec,
    time_cont_year: year, time_cont_month: month, time_cont_day: day };
}

// A controller has one RTC. Keep displaced controller/zone drafts in the owning
// controller's separate draft category, never as settings/readback or writes.
// Return the changed sources so the page can persist their cleared caches too.
export function migrateControllerClockDrafts(controller, selectedParent) {
  if (!supportsSystemClockRelocation(controller)) return [];
  const parents = [controller, ...Object.values(controller.zones ?? {})];
  const sources = parents.filter((parent) =>
    parent.temp_settings?.timing?._date_changed || parent.temp_settings?.timing?._time_changed);
  if (!sources.length) return [];
  controller.temp_settings.system ??= {};
  const destination = controller.temp_settings.system;
  for (const [flag, fields] of [['_date_changed', controllerDateFields], ['_time_changed', controllerTimeFields]]) {
    const candidates = sources.filter((parent) => parent.temp_settings.timing[flag]);
    const selected = candidates.includes(selectedParent) ? selectedParent : null;
    const equal = candidates.every((parent) => fields.every((key) =>
      parent.temp_settings.timing[key] === candidates[0].temp_settings.timing[key]));
    const source = selected ?? (equal ? candidates[0] : null);
    // Existing destination edits win as in the CO2 relocation.
    const move = source && !destination[flag] && !fields.some((key) => key in destination);
    if (move) {
      fields.forEach((key) => { destination[key] = source.temp_settings.timing[key]; });
      destination[flag] = true;
    }
    for (const parent of candidates) {
      if (parent !== source || !move) {
        controller.temp_settings.system_clock_drafts ??= {};
        controller.temp_settings.system_clock_drafts[parent.id] ??= {};
        const archive = controller.temp_settings.system_clock_drafts[parent.id];
        if (!archive[flag]) {
          fields.forEach((key) => { archive[key] = parent.temp_settings.timing[key]; });
          archive[flag] = true;
        }
      }
    }
  }
  for (const parent of sources) {
    parent.temp_settings.timing = withoutControllerClock(parent.temp_settings.timing);
    if (!Object.keys(parent.temp_settings.timing).length) delete parent.temp_settings.timing;
  }
  if (!Object.keys(destination).length) delete controller.temp_settings.system;
  return sources;
}

export function getSystemRestartDisabledReason(controller, action) {
  if (!supportsSystemInfoControls(controller)) return 'disabled_action_firmware_not_supported';
  if (!controller.access_rights?.timing?.write) return 'disabled_action_no_write_access';
  const status = controller.connection?.status;
  // ESP remains reachable when SAM values are unavailable (the amber state).
  if (status !== 'good' && !(action === 'action_restart_wifi_module' && status === 'issues'))
    return 'disabled_action_waiting_for_connection';
  return '';
}

export function getMaincontrollerFlashHealth(value, t) {
  const key = {
    1: 'maincontroller_flash_ok',
    2: 'maincontroller_flash_degraded',
    3: 'maincontroller_flash_disabled',
    4: 'maincontroller_flash_not_present'
  }[value];
  return t(key ?? 'unknown');
}

export function supportsRootSenseFirmwareVersion(controller) {
  return Number(controller?.type) === 5055 &&
    getControllerApiVersion(controller).localeCompare(ROOTSENSE_FIRMWARE_API_MIN) >= 0;
}

export function getRootSenseLinkFirmwareVersion(controller) {
  const system = controller?.settings?.system;
  if (!supportsRootSenseFirmwareVersion(controller) || !(system?.root_sense_link_fw_flags & 1))
    return null;
  if (!(system.root_sense_link_fw_flags & 2) ||
      !(system.root_sense_link_fw_major || system.root_sense_link_fw_minor || system.root_sense_link_fw_patch))
    return '';
  return `1400${system.root_sense_link_hw_variant || 1}.${system.root_sense_link_fw_major}.${system.root_sense_link_fw_minor}.${system.root_sense_link_fw_patch}`;
}

// ESP-based controllers obtain recovery firmware choices from /api/versions.
// Industrial Pro obtains its choices from settings and keeps its existing flow.
export function canOpenFirmwareRecovery(controller) {
  return (
    [5052, 5053, 5055, 5155].includes(controller?.type) &&
    (controller.access_rights?.system ?? { read: true }).read === true
  );
}

function getLiveFirmwareControllerType(controller) {
  const system = controller?.settings?.system ?? controller?.system;
  const mainControllerFirmware = String(system?.fw_version_number ?? '').trim();
  const wifiFirmware = String(system?.esp32_firmware_name ?? '').trim();

  if (/^505[235](?:\.|pre)/i.test(mainControllerFirmware) || /^505w(?:\.|pre)/i.test(wifiFirmware)) {
    return 5055;
  }
  if (/^5155(?:\.|pre)/i.test(mainControllerFirmware) || /^515w(?:\.|pre)/i.test(wifiFirmware)) {
    return 5155;
  }
  return null;
}

export function getFirmwareUpdateOtaInfoKey(controller) {
  const controllerType = getLiveFirmwareControllerType(controller) ?? Number(controller?.type);
  if ([5052, 5053, 5055].includes(controllerType)) return 'fw_update_ota_info_505x';
  if (controllerType === 5155) return 'fw_update_ota_info';
  return null;
}

export function shouldShowFilesystemStatus(controller) {
  return !(
    Number(controller?.type) === 5155 &&
    getControllerApiVersion(controller).localeCompare(ESP32_S3_INTERNAL_STORAGE_API_MIN) >= 0
  );
}
